import {
  DomainError, defaultSettings, withCustomPackage, toInstant, overlapPairs, makeBlock, makeCustomer,
  createRecord, placeHold, extendHold, releaseHold, confirm, adjustPricing, recordPayment, recordRefund, voidPayment,
  collectDeposit, editDetails, startCheckout, settleDeposit, complete, cancel, markLost, reopen, setNotes, setFollowUp, setGuests,
  addDoc, logComm, appendPaymentForReview,
  type BookingRecord, type Block, type Customer, type Ctx, type Expense, type Investment, type Settings, type Result,
} from '@nandanam/domain';
import type { Op } from './types';

export interface State {
  settings: Settings;
  customers: Customer[];
  records: BookingRecord[];
  blocks: Block[];
  expenses: Expense[];
  investments: Investment[];
}

export interface Rejected { op: Op; reason: string; code: string }
export interface EditConflict { entity: string; opIds: [string, string]; cmd: string }
export interface Review { recordId: string; opId: string; reason: string }

export interface Materialized {
  state: State;
  /** Ops that could not be applied (e.g. a hold that lost a race). Kept in full for the owner. */
  rejected: Rejected[];
  /** Same entity edited on two devices without seeing each other's change. Later one applied. */
  edits: EditConflict[];
  /** Records needing a human look (e.g. two payments that together exceed the total). */
  reviews: Review[];
  /** Holds/bookings/blocks that overlap. */
  clashes: { a: string; b: string; kind: 'record' | 'block' }[];
  /** recordId → display number and whether it can still change. */
  numbers: Map<string, { number: string; provisional: boolean }>;
  /** Last applied op per entity (the `basis` for the next local op). */
  heads: Map<string, string>;
  /** opIds whose rejection/edit/review the owner has marked as dealt with ("review.ack"). */
  acked: Set<string>;
  /** opIds applied, in order. */
  applied: string[];
}

export interface OrderedOp { op: Op; createdTime: string | null }

/** Edits that replace a value (vs. additive money/document events). */
export const EDIT_CMDS = new Set([
  'record.adjustPricing', 'record.notes', 'record.followUp', 'record.guests', 'record.edit', 'customer.update', 'settings.set',
  'expense.update', 'block.remove',
]);

const SETTLE_MS = 10 * 60_000;

type Handler = (s: State, args: any, ctx: Ctx, op: Op, m: Materialized) => void;

function rec(s: State, id: string): BookingRecord {
  const r = s.records.find(x => x.id === id);
  if (!r) throw new DomainError('not-found', 'Record not found');
  return r;
}
function put(s: State, r: Result<BookingRecord> | BookingRecord) {
  const v = 'value' in r ? r.value : r;
  const i = s.records.findIndex(x => x.id === v.id);
  if (i < 0) s.records.push(v); else s.records[i] = v;
}
const world = (s: State) => ({ records: s.records, blocks: s.blocks });

function setPath(obj: any, path: string, value: unknown) {
  const ks = path.split('.');
  let o = obj;
  for (const k of ks.slice(0, -1)) {
    if (o[k] === undefined || typeof o[k] !== 'object') throw new DomainError('invalid', `Unknown setting ${path}`);
    o = o[k];
  }
  const last = ks[ks.length - 1]!;
  if (!(last in o)) throw new DomainError('invalid', `Unknown setting ${path}`);
  o[last] = value;
}

/** Ids inside an op are derived from the opId so every device replays to identical state. */
export const subId = (op: Op, n: string | number) => `${op.opId}.${n}`;

export const HANDLERS: Record<string, Handler> = {
  'customer.create': (s, a, ctx, op) => {
    if (s.customers.some(c => c.id === a.id)) return;
    s.customers.push(makeCustomer({ ...a, id: a.id ?? op.opId }, ctx));
  },
  'customer.update': (s, a) => {
    const c = s.customers.find(x => x.id === a.id) ?? (() => { throw new DomainError('not-found', 'Customer not found'); })();
    const { id: _ignore, ...patch } = a.patch ?? {};
    Object.assign(c, patch);
  },
  'record.create': (s, a, ctx, op) => {
    if (s.records.some(r => r.id === a.id)) return;
    const input = { ...a, advance: a.advance ? { ...a.advance, id: a.advance.id ?? subId(op, 'adv') } : null };
    put(s, createRecord(input, world(s), ctx));
  },
  'record.placeHold': (s, a, ctx) => put(s, placeHold(rec(s, a.id), a.hours, world(s), ctx)),
  'record.extendHold': (s, a, ctx) => put(s, extendHold(rec(s, a.id), a.hours, ctx)),
  'record.releaseHold': (s, a, ctx) => put(s, releaseHold(rec(s, a.id), a.reason ?? '', ctx)),
  'record.confirm': (s, a, ctx, op) => put(s, confirm(rec(s, a.id), { pricing: a.pricing, advance: a.advance ? { ...a.advance, id: a.advance.id ?? subId(op, 'adv') } : null }, world(s), ctx)),
  'record.adjustPricing': (s, a, ctx) => put(s, adjustPricing(rec(s, a.id), a.patch, ctx)),
  'record.payment': (s, a, ctx, op, m) => {
    const r = rec(s, a.id), p = { ...a.payment, id: a.payment.id ?? subId(op, 'pay') };
    try { put(s, recordPayment(r, p, ctx)); }
    catch (e) {
      if (!(e instanceof DomainError) || e.code !== 'over-balance') throw e;
      put(s, appendPaymentForReview(r, p, ctx));
      m.reviews.push({ recordId: r.id, opId: op.opId, reason: 'Payments recorded on two devices exceed the total — check for a duplicate' });
    }
  },
  'record.refund': (s, a, ctx, op) => put(s, recordRefund(rec(s, a.id), { ...a.refund, id: a.refund.id ?? subId(op, 'ref') }, ctx)),
  'record.voidPayment': (s, a, ctx) => put(s, voidPayment(rec(s, a.id), a.paymentId, a.reason, ctx)),
  'record.collectDeposit': (s, a, ctx) => put(s, collectDeposit(rec(s, a.id), a.deposit, ctx)),
  'record.checkout': (s, a, ctx) => put(s, startCheckout(rec(s, a.id), ctx)),
  'record.settleDeposit': (s, a, ctx, op) => put(s, settleDeposit(rec(s, a.id), { ...a.settle, deductions: (a.settle.deductions ?? []).map((d: any, i: number) => ({ ...d, id: d.id ?? subId(op, `d${i}`) })) }, ctx)),
  'record.complete': (s, a, ctx) => put(s, complete(rec(s, a.id), ctx)),
  'record.cancel': (s, a, ctx, op) => put(s, cancel(rec(s, a.id), { ...a.cancel, refundId: a.cancel.refundId ?? subId(op, 'ref') }, ctx)),
  'record.markLost': (s, a, ctx) => put(s, markLost(rec(s, a.id), a.reason, ctx)),
  'record.reopen': (s, a, ctx) => put(s, reopen(rec(s, a.id), ctx)),
  'record.notes': (s, a, ctx) => put(s, setNotes(rec(s, a.id), a.notes, ctx)),
  'record.followUp': (s, a, ctx) => put(s, setFollowUp(rec(s, a.id), a.date, ctx)),
  'record.guests': (s, a, ctx) => put(s, setGuests(rec(s, a.id), a.guests, ctx)),
  'record.edit': (s, a, ctx) => put(s, editDetails(rec(s, a.id), a.patch, world(s), ctx)),
  'record.addDoc': (s, a, ctx) => put(s, addDoc(rec(s, a.id), a.doc, ctx)),
  'record.logComm': (s, a, ctx, op) => put(s, logComm(rec(s, a.id), { id: a.commId ?? op.opId, key: a.key, action: a.action }, ctx)),
  'block.add': (s, a, ctx, op) => {
    if (s.blocks.some(b => b.id === (a.id ?? op.opId))) return;
    s.blocks.push(makeBlock({ ...a, id: a.id ?? op.opId }, world(s), ctx).value);
  },
  'block.remove': (s, a) => { s.blocks = s.blocks.filter(b => b.id !== a.id); },
  'expense.add': (s, a, _ctx, op) => {
    const id = a.id ?? op.opId;
    if (s.expenses.some(x => x.id === id)) return;
    if (!Number.isSafeInteger(a.amount) || a.amount <= 0) throw new DomainError('invalid', 'Enter an amount');
    s.expenses.push({ notes: '', receipt: null, booking: null, vendor: '', desc: '', voided: null, ...a, id });
  },
  'expense.update': (s, a) => {
    const x = s.expenses.find(e => e.id === a.id) ?? (() => { throw new DomainError('not-found', 'Expense not found'); })();
    const { id: _i, voided: _v, ...patch } = a.patch ?? {};
    Object.assign(x, patch);
  },
  'expense.void': (s, a, ctx) => {
    const x = s.expenses.find(e => e.id === a.id) ?? (() => { throw new DomainError('not-found', 'Expense not found'); })();
    if (!x.voided) x.voided = { at: a.at ?? '', by: ctx.actor, reason: a.reason };
  },
  'investment.add': (s, a, _ctx, op) => {
    const id = a.id ?? op.opId;
    if (s.investments.some(x => x.id === id)) return;
    if (!Number.isSafeInteger(a.amount) || a.amount <= 0) throw new DomainError('invalid', 'Enter an amount');
    s.investments.push({ notes: '', vendor: '', desc: '', voided: null, ...a, id });
  },
  'investment.void': (s, a, ctx) => {
    const x = s.investments.find(e => e.id === a.id) ?? (() => { throw new DomainError('not-found', 'Investment not found'); })();
    if (!x.voided) x.voided = { at: a.at ?? '', by: ctx.actor, reason: a.reason };
  },
  'settings.set': (s, a) => {
    setPath(s.settings, a.path, structuredClone(a.value));
    // Package lists saved before the Customize option existed get it added back.
    if (a.path === 'packages' && Array.isArray(s.settings.packages)) s.settings.packages = withCustomPackage(s.settings.packages);
  },
  // Marks flagged changes as reviewed. Changes nothing in the business data; the ops stay in the log.
  'review.ack': (_s, a, _ctx, _op, m) => { for (const id of a.opIds ?? []) m.acked.add(id); },
};

export function emptyState(settings: Settings = defaultSettings()): State {
  return { settings, customers: [], records: [], blocks: [], expenses: [], investments: [] };
}

/** Server order first (createdTime, then opId); ops not yet on the server go last, in local order. */
export function orderOps(ops: OrderedOp[]): OrderedOp[] {
  return ops.map((o, i) => ({ o, i })).sort((x, y) => {
    const a = x.o.createdTime, b = y.o.createdTime;
    if (a && b) return a < b ? -1 : a > b ? 1 : x.o.op.opId < y.o.op.opId ? -1 : x.o.op.opId > y.o.op.opId ? 1 : 0;
    if (a) return -1;
    if (b) return 1;
    return x.i - y.i;
  }).map(x => x.o);
}

/**
 * Rebuild the business state from the full op log. Deterministic: the same set of ops gives the same
 * state on every device. Nothing is ever dropped silently: an op that cannot apply is listed in
 * `rejected` with its reason.
 */
export function materialize(ops: OrderedOp[], o: { now: number; numberStart: number; latestServerTime?: string | null; initial?: State }): Materialized {
  const m: Materialized = {
    state: structuredClone(o.initial ?? emptyState()), rejected: [], edits: [], reviews: [], clashes: [],
    numbers: new Map(), heads: new Map(), applied: [], acked: new Set(),
  };
  const seen = new Set<string>();
  let next = o.numberStart;
  const latest = o.latestServerTime ? Date.parse(o.latestServerTime) : o.now;
  for (const { op, createdTime } of orderOps(ops)) {
    if (seen.has(op.opId)) continue;
    seen.add(op.opId);
    const h = HANDLERS[op.cmd];
    if (!h) { m.rejected.push({ op, reason: `Unknown change type ${op.cmd} (newer app version?)`, code: 'unknown' }); continue; }
    const head = m.heads.get(op.entity) ?? null;
    const ctx: Ctx = { now: toInstant(op.at), settings: m.state.settings, actor: op.actor };
    const before = structuredClone(m.state);
    try {
      h(m.state, op.args, ctx, op, m);
    } catch (e) {
      m.state = before;
      m.rejected.push({ op, reason: e instanceof Error ? e.message : String(e), code: e instanceof DomainError ? e.code : 'error' });
      continue;
    }
    if (EDIT_CMDS.has(op.cmd) && head !== null && op.basis !== head) m.edits.push({ entity: op.entity, opIds: [head, op.opId], cmd: op.cmd });
    m.heads.set(op.entity, op.opId);
    m.applied.push(op.opId);
    if (op.cmd === 'record.create') {
      const r = m.state.records.find(x => x.id === op.args.id);
      if (r && !createdTime) m.numbers.set(r.id, { number: '', provisional: true });
      else if (r) {
        const num = `NF-${next++}`;
        r.number = num;
        m.numbers.set(r.id, { number: num, provisional: latest - Date.parse(createdTime!) < SETTLE_MS });
      }
    }
  }
  m.rejected = m.rejected.filter(x => !m.acked.has(x.op.opId));
  m.edits = m.edits.filter(x => !m.acked.has(x.opIds[1]));
  m.reviews = m.reviews.filter(x => !m.acked.has(x.opId));
  m.clashes = overlapPairs(world(m.state), { now: o.now, bufferHours: m.state.settings.bufferHours });
  return m;
}
